import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { updateIntervention, createIntervention, 
  emptyIntervention, facturationEntiteOptions,
  finishNewPrestataireCreation, startNewPrestataireCreation,
  cancelNewPrestataireCreation,
  FileFields, 
} from '../interventionsSlice'
import { useBienContext } from '../../biens/BienContext'
import { fetchBien } from '../../biens/biensSlice'
import { InterventionFormContext } from './InterventionFormContext'
import UpdateForm from './UpdateForm'
import PrestataireForm from '../../prestataires/Form'

import useFormConfirmation from '@/hooks/useFormConfirmation.jsx'

export default ({ onClose = () => {}, onSuccess = () => {} }) => {
  const dispatch = useDispatch()
  const bienContext = useBienContext()
  const intervention = useSelector((state) => state.interventions.inEdition || emptyIntervention)
  const errors = useSelector((state) => state.interventions.requestStatus.errors)
  const isCreating = useSelector((state) => state.interventions.loadingStatus.create)
  const isUpdating = useSelector((state) => state.interventions.loadingStatus.update)
  const isLoading = isCreating || isUpdating
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Récupérer le bien depuis le store Redux
  const bienFromStore = useSelector(state => state.biens.current)
  const isCreatingNewPrestataire = useSelector(state => state.interventions.isCreatingNewPrestataire)
  
  // État pour stocker le bien, qu'il vienne du contexte ou de l'intervention
  const [bien, setBien] = useState(null)
  const [isBienFromContext, setIsBienFromContext] = useState(false)
  // Garde une trace de l'ID du bien déjà récupéré pour éviter les requêtes multiples
  const [fetchedBienId, setFetchedBienId] = useState(null)
  const [isFetchingBien, setIsFetchingBien] = useState(false)
  const [entiteFacturableOptions, setEntiteFacturableOptions] = useState([])
  
  const { handleSubmit, watch, reset,
    setValue, getValues, 
    control, formState: { errors: formErrors }
  } = useForm({
    defaultValues: intervention,
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const bienFromForm = watch("bien")

  // Options de facturation basées sur le bien (si disponible)
  useEffect(() => {
    if(bien) {
      setEntiteFacturableOptions(facturationEntiteOptions(bien))
    }
  }, [bien])

  // Effet pour récupérer le bien depuis l'API si nécessaire
  useEffect(() => {
    // Si le bien est déjà disponible dans le contexte, l'utiliser
    if (bienContext?.bien) {
      setBien(bienContext.bien)
      setIsBienFromContext(true)
      return
    }
    
    // Si l'intervention a un bien_id et qu'on n'a pas encore récupéré ce bien spécifique
    if (intervention?.bien_id && fetchedBienId !== intervention.bien_id) {
      // Marquer ce bien_id comme étant en cours de récupération
      setFetchedBienId(intervention.bien_id)
      setIsFetchingBien(true)
      dispatch(fetchBien(intervention.bien_id))
      return
    }

    // Dans le cas de la création d'une intervention depuis la page Gestion
    // hors contexte d'un bien
    if(bienFromForm?.id && fetchedBienId !== bienFromForm.id) {
      setFetchedBienId(bienFromForm.id)
      setIsFetchingBien(true)
      dispatch(fetchBien(bienFromForm.id))
      return
    }
  }, [bienContext?.bien, intervention?.bien_id, bienFromForm?.id, dispatch, fetchedBienId])

  // Effet pour mettre à jour le bien lorsqu'il est récupéré depuis le store Redux
  useEffect(() => {
    if (isFetchingBien && bienFromStore && (intervention?.bien_id === bienFromStore.id || bienFromForm?.id === bienFromStore?.id)) {
      setBien(bienFromStore)
      setIsFetchingBien(false)
    }
  }, [bienFromStore, intervention?.bien_id, bienFromForm?.id, isFetchingBien])


  const onReset = () => {
    reset(intervention)
  }

  // useFormConfirmation feature
  // ajoute les states, le useEffect pour surveiller l'état du formulaire
  // et s'assurer s'il a été modifié ou pas pour afficher une boîte de 
  // confirmation lorsque le bouton "Annuler" est cliqué.
  const {
    handleFormCancel,
    setInitialLoadComplete,
    setInitialValues,
    setFormIsDirty,
    FormConfirmDialog
  } = useFormConfirmation({
    watch,
    getValues,
    onReset,
    onClose
  })

  // Synchroniser le formulaire lorsque intervention change 
  useEffect(() => {
    if (!intervention) return;
    reset(intervention)

    Object.keys(intervention).forEach( key => {
      if(FileFields.includes(key)) { intervention[key] ? setValue(key, intervention[key].filename) : setValue(key, "") }
      if(key in intervention) {
        setValue(key, intervention[key])
      }
    })


    // useFormConfirmation feature
    // Marquer le chargement initial comme terminé et enregistrer les valeurs initiales
    // Utiliser un délai pour s'assurer que le formulaire est complètement mis à jour
    setTimeout(() => {
      setInitialValues(getValues())
      setInitialLoadComplete(true)
      setFormIsDirty(false) // Réinitialiser l'état dirty après le chargement initial
    }, 100)

  }, [intervention])

  // Effet pour détecter les changements dans l'état de chargement
  useEffect(() => {
    if (isSubmitting && !isUpdating && !isCreating) {
      setIsSubmitting(false)
      if (!errors || Object.keys(errors).length === 0) {
        onSuccess()
      }
    }
  }, [isUpdating, isCreating, errors, onSuccess, dispatch])

  /**
   * Récupère le message d'erreur pour un champ donné
   * Combine les erreurs de validation côté client et les erreurs API
   *
   * @param {string} fieldName - Nom du champ
   * @returns {string} Message d'erreur ou chaîne vide
   */
  const getErrorMessage = (fieldName) => {
    const errorsMsg = formErrors?.[fieldName]?.message || errors?.[fieldName] || ''
    if(Array.isArray(errorsMsg)) {
      return errorsMsg.join(", ")
    }
    return errorsMsg
  }

  /**
   * Détermine si un champ a une erreur
   *
   * @param {string} fieldName - Nom du champ
   * @returns {boolean} true si le champ a une erreur, false sinon
   */
  const hasError = (fieldName) =>
    !!formErrors?.[fieldName] || !!errors?.[fieldName]

  /**
   * Gère la soumission du formulaire
   * Dispatche l'action appropriée (create ou update) selon si l'intervention existe déjà
   *
   * @param {Object} formData - Données du formulaire
   */
  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true)

      if (intervention.id) {
        await dispatch(updateIntervention({
          bien_id: bien?.id,
          id: intervention.id,
          data: formData
        }))
      } else {
        await dispatch(createIntervention({
          data: formData,
          bien_id: bien?.id
        }))
      }
    } catch (error) {
      setIsSubmitting(false)
    }
  }

  const handleNewPrestataire = () => {
    dispatch(startNewPrestataireCreation())
  }

  const handlePrestataireCreated = (newPrestataire) => {
    dispatch(finishNewPrestataireCreation(newPrestataire))
  }

  const contextValues = {
    bien,
    control,
    intervention,
    entiteFacturableOptions,
    isLoading,
    getErrorMessage,
    hasError,
    handleNewPrestataire,
    isFetchingBien,
    isBienFromContext
  }

  return (
    <div>
      { isCreatingNewPrestataire ? 
        <PrestataireForm 
          onClose={() => {
            dispatch(cancelNewPrestataireCreation())
          }}
          onPrestataireCreated={handlePrestataireCreated} 
        /> : 
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">
              {intervention.id ? 'Modifier' : 'Ajouter'} une intervention
            </h2>
          </div>

          <div className="card bg-base-100 border border-base-200 rounded-none mb-4 max-h-[calc(80vh-150px)] overflow-y-auto">

            <InterventionFormContext.Provider value={contextValues}>
              <UpdateForm />
            </InterventionFormContext.Provider>
          </div>

          <div className="flex justify-between">
            <button
              type="button"
              className="btn btn-outline"
              onClick={handleFormCancel}
            >
              Annuler
            </button>
            <button
              type="submit"
              className={`btn ${isLoading ? 'loading' : ''} btn-primary text-white p-2 mt-4`}
              disabled={isLoading}
            >
              {isLoading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      }
      <FormConfirmDialog />
    </div>
  )
}